import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createConstraint,
  createMealExclusion,
  deleteConstraint,
  deleteMealExclusion,
  getConstraints,
  getMealExclusions,
  getMealExclusionsByMeal,
} from '@/lib/api/constraints';
import type { CreateConstraintRequest, CreateExclusionRequest } from '@/types/api';
import { constraintKeys, exclusionKeys } from '@/lib/query-keys';

export function useConstraints(userId?: number) {
  return useQuery({
    queryKey: constraintKeys.list(userId),
    queryFn: () => getConstraints(userId),
  });
}

export function useCreateConstraint() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateConstraintRequest) => createConstraint(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: constraintKeys.all });
    },
  });
}

export function useDeleteConstraint() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteConstraint(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: constraintKeys.all });
    },
  });
}

export function useExclusions(userId?: number) {
  return useQuery({
    queryKey: exclusionKeys.list(userId),
    queryFn: () => getMealExclusions(userId),
  });
}

export function useExclusionsByMeal(mealId: number) {
  return useQuery({
    queryKey: exclusionKeys.byMeal(mealId),
    queryFn: () => getMealExclusionsByMeal(mealId),
  });
}

export function useCreateMealExclusion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateExclusionRequest) => createMealExclusion(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: exclusionKeys.all });
    },
  });
}

export function useDeleteMealExclusion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteMealExclusion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: exclusionKeys.all });
    },
  });
}
